import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { trackUserAction, trackSafetyEvent } from '../../utils/analytics';
import { searchPreflight, getPreflightFailureKind, SEARCH_PREFLIGHT_SERVICE_ERROR_MESSAGE } from '../../utils/searchPreflight';
import { notifySearchLicenseRequired } from '../../utils/searchLicenseNotify';
import { getCurrentUser } from '../../services/firebase';
import { loadSearchFeatureEnabled } from '../../utils/searchFeaturePreferencesStore';
import { prefixes, topics } from './HomePage.constants';

export const useHomePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('web');
  const [activeCategory, setActiveCategory] = useState(null);
  const [showSuggestionBar, setShowSuggestionBar] = useState(false);
  const [showSuggestionsList, setShowSuggestionsList] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const profileRef = useRef(null);
  const searchInputRef = useRef(null);
  const autoSearchFromUrl = useRef(false);

  // Profile dropdown click outside handler
  useEffect(() => {
    if (!profileOpen) return;
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  // On mount, check for ?q= in URL and set search box
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    const type = params.get('type') || 'web';
    
    if (q) {
      setSearchQuery(q);
      setActiveSearch(type);
      if (searchInputRef.current) {
        searchInputRef.current.value = q;
      }
      autoSearchFromUrl.current = true;
    }
  }, [location.search]);

  // When searchQuery changes, if autoSearchFromUrl is set, trigger search
  useEffect(() => {
    if (autoSearchFromUrl.current && searchQuery.trim()) {
      const fakeEvent = { preventDefault: () => {} };
      handleSearch(fakeEvent);
      autoSearchFromUrl.current = false;
    }
  }, [searchQuery]);

  // Handle search submission
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!loadSearchFeatureEnabled()) {
      toast.error('Search is turned off on this device. A parent can turn it on in Settings.');
      return;
    }
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    // Track search submission
    trackUserAction.searchSubmitted(activeSearch);
    
    // Check keyword blocking before proceeding
    const user = getCurrentUser();
    const firebaseId = user ? user.uid : null;
    
    try {
      const preflight = await searchPreflight(searchQuery.trim());
      
      if (preflight.reason === 'auth_required') {
        toast.error('Sign in with your Kiddoware account to search.');
        navigate('/auth');
        return;
      }
      if (preflight.reason === 'license_required') {
        notifySearchLicenseRequired();
        return;
      }
      const failureKind = getPreflightFailureKind(preflight);
      if (failureKind === 'service_error') {
        toast.error(SEARCH_PREFLIGHT_SERVICE_ERROR_MESSAGE);
        return;
      }
      if (failureKind === 'keyword_blocked') {
        toast.error(`Search not allowed: "${preflight.search_keyword}". Try a different query.`);
        trackSafetyEvent.keywordBlocked(searchQuery.trim(), firebaseId);
        return;
      }
      
      // Keyword is allowed, proceed with search
      const searchPath = activeSearch === 'web' ? '/s' : `/${activeSearch === 'game' ? 'g' : activeSearch === 'education' ? 'e' : activeSearch === 'facts' ? 'f' : activeSearch === 'images' ? 'i' : activeSearch === 'news' ? 'n' : 'v'}`;
      navigate(`${searchPath}?q=${encodeURIComponent(searchQuery.trim())}&type=${activeSearch}`);
    } catch (error) {
      console.error('Keyword check failed:', error);
      // Proceed with search if keyword check fails
      const searchPath = activeSearch === 'web' ? '/s' : `/${activeSearch === 'game' ? 'g' : activeSearch === 'education' ? 'e' : activeSearch === 'facts' ? 'f' : activeSearch === 'images' ? 'i' : activeSearch === 'news' ? 'n' : 'v'}`;
      navigate(`${searchPath}?q=${encodeURIComponent(searchQuery.trim())}&type=${activeSearch}`);
    }
  };

  /** Jump into web search from a world card using the same safety checks as the main search. */
  const handleExploreTopic = async (rawQuery) => {
    const q = String(rawQuery || '').trim();
    if (!loadSearchFeatureEnabled()) {
      toast.error('Search is turned off on this device. A parent can turn it on in Settings.');
      return;
    }
    if (!q) {
      toast.error('Please enter a search term');
      return;
    }

    trackUserAction.searchSubmitted('web');
    const currentUser = getCurrentUser();
    const firebaseId = currentUser ? currentUser.uid : null;

    try {
      const preflight = await searchPreflight(q);

      if (preflight.reason === 'auth_required') {
        toast.error('Sign in with your Kiddoware account to search.');
        navigate('/auth');
        return;
      }
      if (preflight.reason === 'license_required') {
        notifySearchLicenseRequired();
        return;
      }
      const failureKind = getPreflightFailureKind(preflight);
      if (failureKind === 'service_error') {
        toast.error(SEARCH_PREFLIGHT_SERVICE_ERROR_MESSAGE);
        return;
      }
      if (failureKind === 'keyword_blocked') {
        toast.error(`Search not allowed: "${preflight.search_keyword}". Try a different query.`);
        trackSafetyEvent.keywordBlocked(q, firebaseId);
        return;
      }

      navigate(`/s?q=${encodeURIComponent(q)}&type=web`);
    } catch (error) {
      console.error('Keyword check failed:', error);
      navigate(`/s?q=${encodeURIComponent(q)}&type=web`);
    }
  };

  // Handle navigation clicks
  const handleNavigationClick = (destination) => {
    trackUserAction.navigationClick(destination);
  };

  // Handle suggestion button clicks
  const handleSuggestionClick = (category, event) => {
    // Prevent form submission
    event.preventDefault();
    event.stopPropagation();

    // Use setTimeout to ensure state updates happen after focus event
    setTimeout(() => {
      setActiveCategory(category);
      setShowSuggestionBar(false);
      setShowSuggestionsList(true);

      const prefix = prefixes[category];
      
      if (searchInputRef.current) {
        searchInputRef.current.value = prefix;
        setSearchQuery(prefix);
        searchInputRef.current.focus();
      }
    }, 10);
  };

  // Handle topic selection
  const handleTopicSelect = (topic, event) => {
    // Prevent form submission
    event.preventDefault();
    event.stopPropagation();

    const message = prefixes[activeCategory] + topic;
    
    if (searchInputRef.current) {
      searchInputRef.current.value = message;
      setSearchQuery(message);
      searchInputRef.current.focus();
    }
    setShowSuggestionsList(false);
    setActiveCategory(null);
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    setShowSuggestionBar(true);
  };

  // Handle clicking outside to hide suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!searchInputRef.current || searchInputRef.current.contains(event.target)) return;

      const suggestionBar = document.getElementById('suggestion-bar');
      const suggestionsList = document.getElementById('suggestions-list');

      if (!suggestionBar || !suggestionsList) {
        setShowSuggestionBar(false);
        setShowSuggestionsList(false);
        setActiveCategory(null);
        return;
      }

      if (!suggestionBar.contains(event.target) && !suggestionsList.contains(event.target)) {
        setShowSuggestionBar(false);
        setShowSuggestionsList(false);
        setActiveCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestionsList, activeCategory, showSuggestionBar]);

  // Add this useEffect to hide the topics bar if clicked anywhere outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (!showSuggestionsList) return;
      const input = searchInputRef.current;
      const suggestionsList = document.getElementById('suggestions-list');
      if (!suggestionsList) {
        setShowSuggestionsList(false);
        setActiveCategory(null);
        return;
      }
      if (!suggestionsList.contains(event.target) && input && !input.contains(event.target)) {
        setShowSuggestionsList(false);
        setActiveCategory(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestionsList]);

  return {
    // State
    searchQuery,
    activeSearch,
    activeCategory,
    showSuggestionBar,
    showSuggestionsList,
    profileOpen,
    user,
    loading,
    
    // Refs
    profileRef,
    searchInputRef,
    
    // Functions
    setSearchQuery,
    setActiveSearch,
    setActiveCategory,
    setShowSuggestionBar,
    setShowSuggestionsList,
    setProfileOpen,
    handleSearch,
    handleNavigationClick,
    handleSuggestionClick,
    handleTopicSelect,
    handleSearchFocus,
    handleExploreTopic,
    
    // Navigation
    navigate,
    
    // Constants
    prefixes,
    topics,
  };
}; 